import React, { useState, useEffect } from 'react';
import { surveyApiService } from '../../api/surveyApi';
import { useNavigate } from 'react-router-dom';
import {
    CheckCircleIcon,
    ClockIcon,
    EyeIcon,
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import DataTable from '../../components/ui/DataTable';
import TableAction, { TableActionGroup } from '../../components/ui/TableAction';

const SurveyGlobalSubmissions = () => {
    const [submissions, setSubmissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchAllSubmissions = async () => {
            try {
                const response = await surveyApiService.getSubmissions();
                const data = Array.isArray(response) ? response : (response?.results || []);
                setSubmissions(data);
            } catch (error) {
                console.error('Error fetching global submissions:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchAllSubmissions();
    }, []);

    const columns = [
        {
            header: 'ANKET',
            accessorKey: 'survey_title',
            size: 300,
            cell: ({ row }) => {
                const sub = row.original;
                return (
                    <div className="flex flex-col">
                        <span className="text-sm font-bold text-slate-900 dark:text-white leading-tight">
                            {sub.survey_title?.tr || Object.values(sub.survey_title || {})[0] || 'İsimsiz Anket'}
                        </span>
                        <span className="text-[10px] text-slate-400 font-medium tracking-tight uppercase">Versiyon: {sub.survey_version || 1}</span>
                    </div>
                );
            }
        },
        {
            header: 'DURUM',
            accessorKey: 'is_completed',
            size: 140,
            cellClassName: 'text-center',
            cell: ({ getValue }) => {
                const isCompleted = getValue();
                return (
                    <div className="flex justify-center">
                        {isCompleted ? (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded bg-emerald-50 dark:bg-emerald-500/10 text-[10px] font-bold text-emerald-600 dark:text-emerald-500 border border-emerald-100 dark:border-emerald-500/20">
                                <CheckCircleIcon className="w-3 h-3" />
                                TAMAMLANDI
                            </span>
                        ) : (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded bg-amber-50 dark:bg-amber-500/10 text-[10px] font-bold text-amber-600 dark:text-amber-500 border border-amber-100 dark:border-amber-500/20">
                                <ClockIcon className="w-3 h-3" />
                                KISMI
                            </span>
                        )}
                    </div>
                );
            }
        },
        {
            header: 'IP ADRESİ',
            accessorKey: 'ip_address',
            size: 150,
            cellClassName: 'text-center',
            cell: ({ getValue }) => (
                <span className="text-xs font-medium font-mono text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800/50 px-2 py-0.5 rounded border border-slate-100 dark:border-slate-800/30">
                    {getValue() || '---.---.---.---'}
                </span>
            )
        },
        {
            header: 'TARİH / SAAT',
            accessorKey: 'started_at',
            size: 150,
            cellClassName: 'text-center',
            cell: ({ getValue }) => (
                <div className="flex flex-col items-center">
                    <span className="text-xs font-bold text-slate-700 dark:text-slate-300 tracking-tight">
                        {getValue() ? format(new Date(getValue()), 'dd.MM.yyyy', { locale: tr }) : '-'}
                    </span>
                    <span className="text-[10px] text-slate-400 font-medium">
                        {getValue() ? format(new Date(getValue()), 'HH:mm', { locale: tr }) : '-'}
                    </span>
                </div>
            )
        },
        {
            header: 'İŞLEMLER',
            id: 'actions',
            size: 80,
            cell: ({ row }) => (
                <TableActionGroup justify="end">
                    <TableAction
                        icon={EyeIcon}
                        onClick={() => navigate(`/survey/submissions/detail/${row.original.id}`)}
                        color="blue"
                        title="İncele"
                    />
                </TableActionGroup>
            )
        }
    ];

    return (
        <div className="p-8 max-w-7xl mx-auto space-y-8">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 border-b border-slate-200 dark:border-slate-800 pb-8">
                <div className="space-y-1">
                    <h1 className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight italic">Tüm Yanıtlar</h1>
                    <p className="text-sm text-slate-500 font-medium tracking-tight uppercase tracking-widest text-[10px]">Sistem üzerindeki tüm anket katılımlarının dökümü</p>
                </div>
            </div>

            <DataTable
                data={submissions}
                columns={columns}
                isLoading={loading}
            />
        </div>
    );
};

export default SurveyGlobalSubmissions;
